import { Dictionary } from '@reduxjs/toolkit';

import { all, call, put, select } from 'redux-saga/effects';

import { selectAuthSession } from '../../auth/store/selectors';
import Logger from '../../utils/logger';
import { debounce } from '../../utils/saga-helpers';
import { selectCurrentCourse } from '../courses/selectors';
import { API } from '../shared/api-responses';
import { CourseRole } from '../shared/types';
import { fetchCourseUserProfiles, setCourseUserProfiles } from './actions';
import { fetchCourseUserProfiles as fetchCourseUserProfilesAPI } from './api';
import { selectCourseUserProfilesById } from './selectors';
import { CourseUserProfile } from './types';

const logger = Logger.create('db/user-profiles');

function* fetchCourseUserProfilesWorker(...actions: ReturnType<typeof fetchCourseUserProfiles>[]) {
  try {
    const session: YieldSelectorType<typeof selectAuthSession> = yield select(selectAuthSession);

    if (!session) {
      logger.warn(`S3 bucket URL is not set`);
      return;
    }

    const currentCourse: YieldSelectorType<typeof selectCurrentCourse> = yield select(selectCurrentCourse);

    if (!currentCourse) {
      console.warn(`Course is not selected`);
      return;
    }

    const existingProfilesById: YieldSelectorType<typeof selectCourseUserProfilesById> = yield select(
      selectCourseUserProfilesById
    );

    const allIncomingPayloads = actions.flatMap((a) => a.payload);

    const avatarsMap = new Map<
      MongoId,
      { isEmailIdNeeded: boolean; isCourseRoleNeeded: boolean; existingCourseRole: CourseRole | '' }
    >();

    // merge different requests with same avatarId
    for (const { avatarId, isEmailIdNeeded, ...rest } of allIncomingPayloads) {
      const flags = avatarsMap.get(avatarId);

      // Merge flags: always keep most demanding version
      const mergedFlags = {
        isEmailIdNeeded: isEmailIdNeeded || flags?.isEmailIdNeeded || false,
        isCourseRoleNeeded: rest.isCourseRoleNeeded || flags?.isCourseRoleNeeded || false,
        existingCourseRole: rest.isCourseRoleNeeded
          ? rest.existingCourseRole ?? ''
          : flags?.existingCourseRole ?? '',
      };

      avatarsMap.set(avatarId, mergedFlags);
    }

    const requestsMap = new Map<`${boolean}-${boolean}`, API.FetchCourseUserProfilesRequest>();

    for (const [avatarId, { isEmailIdNeeded, isCourseRoleNeeded, existingCourseRole }] of Array.from(
      avatarsMap.entries()
    )) {
      /** Filtering out existing profiles before batching requests */

      const existingProfile = existingProfilesById[avatarId];

      const isMissingEmail = isEmailIdNeeded && !existingProfile?.emailId;
      const isMissingCourseRole =
        isCourseRoleNeeded && !existingProfile?.courseUserDetails[currentCourse.id]?.role;

      const isFetchNeeded = !existingProfile || isMissingEmail || isMissingCourseRole;
      if (!isFetchNeeded) continue;

      /** Batching requests based on flags */

      const key = `${isEmailIdNeeded}-${isCourseRoleNeeded}` as const;
      const group = requestsMap.get(key);

      if (group) {
        group.avatarIdAndCourseRoleDictionary[avatarId] = existingCourseRole;
      } else {
        requestsMap.set(key, {
          isEmailIdNeeded,
          isCourseRoleNeeded,
          avatarIdAndCourseRoleDictionary: { [avatarId]: existingCourseRole },
        });
      }
    }

    const groupedPayloads = Array.from(requestsMap.values());

    // fetch profiles in batches
    const fetchedProfileBatches: YieldCallType<typeof fetchCourseUserProfilesAPI>[] = yield all(
      groupedPayloads.map((payload) =>
        call(fetchCourseUserProfilesAPI, session.s3BucketURL, currentCourse.id, payload)
      )
    );

    // merge fetched profiles
    const mergedUserProfiles = fetchedProfileBatches.reduce<Dictionary<CourseUserProfile>>(
      (merged, currentBatch) => {
        return { ...merged, ...currentBatch };
      },
      {}
    );

    yield put(setCourseUserProfiles({ userProfiles: mergedUserProfiles }));
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootUserProfilesSaga() {
  yield debounce(200, fetchCourseUserProfiles, fetchCourseUserProfilesWorker);
}
